import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Fetcher, FetcherApi, ServerResponse } from './Fetcher';

export type errorType = (
  error: ServerResponse<any>,
) => Observable<ServerResponse<any>>;

export interface BaseClientApi {
  get<T>(
    url: string,
    params: object | null,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T>;

  post<T>(
    url: string,
    params?: object | File,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T>;

  put<T>(
    url: string,
    params?: object,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T>;
}

export abstract class BaseClient implements BaseClientApi {
  private fetchModel: FetcherApi | any;

  private handleError = (error: {
    status: string;
    code: number;
    data?: any;
    response?: any;
    validation?: any;
  }) => {
    //@TODO: seems this error handling to be reviewed. Should we handle 'status' and 'code' as strings or numbers?
    if (
      error.status === 'fail' ||
      error.status === 'error' ||
      `${error.status}` === '0' ||
      Number(error.status) >= 400
    ) {
      if (Number(error.code) === 400) {
        error['validation'] = { ...error.data.data };
      }

      return throwError({
        ...(error.response || error),
        status: error.status, // we should work only with server status codes (200, 300, 400...)
      });
    }

    return of(error);
  };

  constructor(private apiUrl: string) {
    this.setHttpModel();
  }

  private setHttpModel() {
    this.fetchModel = Fetcher;
  }

  get<T>(
    url: string,
    params: object | null = null,
    entityName?: string,
    headers?: HeadersInit,
  ): Observable<T> {
    if (url.startsWith('/')) {
      url = url.substring(1);
    }
    const request = this.fetchModel.get(
      `${this.apiUrl}/${url}`,
      params,
      entityName,
      headers,
    );
    return request.pipe(catchError((err) => this.handleError(err)));
  }

  post<T>(
    url: string,
    params?: object | string | File,
    entityName?: string,
    headers?: Headers | string[][] | Record<string, string>,
  ): Observable<T> {
    if (url.startsWith('/')) {
      url = url.substring(1);
    }

    const request = this.fetchModel.post(
      `${this.apiUrl}/${url}`,
      params,
      null,
      headers,
    );

    return request.pipe(catchError((err) => this.handleError(err)));
  }

  put<T>(
    url: string,
    params?: object | string,
    entityName?: string,
    headers?: Headers | string[][] | Record<string, string>,
  ): Observable<T> {
    if (url.startsWith('/')) {
      url = url.substring(1);
    }
    const request = this.fetchModel.put(
      `${this.apiUrl}/${url}`,
      params,
      null,
      headers,
    );
    return request.pipe(catchError((err) => this.handleError(err)));
  }

  getWithoutApiUrl<T>(
    url: string,
    params?: object | string,
    entityName?: string,
    headers?: Headers | string[][] | Record<string, string>,
  ): Observable<T> {
    if (url.startsWith('/')) {
      url = url.substring(1);
    }
    const request = this.fetchModel.get(url, params, null, headers);
    return request.pipe(catchError((err) => this.handleError(err)));
  }

  postWithoutApiUrl<T>(
    url: string,
    params?: object | string,
    entityName?: string,
    headers?: Headers | string[][] | Record<string, string>,
  ): Observable<T> {
    if (url.startsWith('/')) {
      url = url.substring(1);
    }
    const request = this.fetchModel.post(url, params, null, headers);
    return request.pipe(catchError((err) => this.handleError(err)));
  }
}
